import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../../../api/client";
import Icon from "../Icon";
import { isVideoFile, vimeoEmbed } from "../RecordingPlayer";
import { Pill } from "./staffUi";

// Times from the API are WAT wall-clock strings ("2026-10-16T19:00"), shown as written.
export const watDay = (s) => (s ? new Date(`${s.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).replace(",", "") : "");
export const watTime = (s) => { if (!s) return ""; const [h, m] = s.slice(11, 16).split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
export const watWhen = (s) => (s ? `${watDay(s)} · ${watTime(s)} WAT` : "");
const errMsg = (e, fallback) => {
  const d = e?.response?.data;
  return (d?.errors && Object.values(d.errors)[0]?.[0]) || d?.message || fallback;
};

function Modal({ title, children, ok, danger, busy, onCancel, onSubmit }) {
  return (
    <div className="sp-modal-wrap">
      <form className="sp-modal" role="dialog" aria-modal="true" aria-labelledby="wm-t" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <h3 id="wm-t">{title}</h3>
        {children}
        <div className="sp-acts" style={{ justifyContent: "flex-end" }}>
          <button className="cbtn quiet" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className={`cbtn ${danger ? "danger" : "go"}`} type="submit" disabled={busy}>{busy ? "Saving…" : ok}</button>
        </div>
      </form>
    </div>
  );
}

/** Add a file or a link to a class or to the week. */
function AddMaterial({ url, onAdded }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Give the material a name students will recognise.");
    if (!file && !link.trim()) return toast.error("Choose a file or paste a link.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      if (file) fd.append("file", file); else fd.append("link", link.trim());
      await client.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Material added");
      setOpen(false); setName(""); setLink(""); setFile(null);
      onAdded();
    } catch (e) {
      toast.error(errMsg(e, "We couldn't add that material."));
    }
    setBusy(false);
    return undefined;
  };
  if (!open) return <button type="button" className="cbtn quiet" onClick={() => setOpen(true)}>+ Add material</button>;
  return (
    <div className="sp-addmat">
      <input aria-label="Material name" placeholder="Name, e.g. Week 3 slides" value={name} onChange={(e) => setName(e.target.value)} />
      <input aria-label="Link" placeholder="Paste a link…" value={link} onChange={(e) => setLink(e.target.value)} disabled={!!file} />
      <label className="cbtn quiet" style={{ cursor: "pointer" }}>{file ? file.name.slice(0, 24) : "…or choose a file"}<input type="file" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} /></label>
      <button type="button" className="cbtn go" onClick={save} disabled={busy}>{busy ? "Adding…" : "Add"}</button>
      <button type="button" className="link-btn" onClick={() => setOpen(false)}>Cancel</button>
    </div>
  );
}

function Materials({ items, removeUrl, onChange }) {
  const remove = async (m) => {
    try { await client.delete(removeUrl(m.id)); toast.success("Material removed"); onChange(); } catch (e) { toast.error(errMsg(e, "We couldn't remove it.")); }
  };
  if (!items.length) return <p className="course-tag" style={{ margin: 0 }}>No materials yet.</p>;
  return (
    <div className="sp-files">
      {items.map((m) => (
        <div key={m.id} className="sp-file">
          <span className="sp-fico"><Icon name={m.is_file ? "files" : "link"} size={16} /></span>
          <a href={m.url} target="_blank" rel="noopener noreferrer">{m.name}</a>
          <button type="button" className="link-btn danger" onClick={() => remove(m)}>Remove</button>
        </div>
      ))}
    </div>
  );
}

/** Assignment from a class, with its rubric. */
function Assignment({ cls, onSaved }) {
  const a = cls.assignment;
  const [f, setF] = useState(a ? { title: a.title || "", description: a.description || "", due: a.due || "", grace: a.grace || "", rubric: a.rubric?.length ? a.rubric : [] } : null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const setCrit = (i, k, v) => set("rubric", f.rubric.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  if (!f) return <button type="button" className="cbtn quiet" onClick={() => setF({ title: "", description: "", due: "", grace: "", rubric: [{ criterion: "Correct output", marks: 10 }, { criterion: "Clear explanation", marks: 10 }] })}>+ Add an assignment to this class</button>;

  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", f.title);
      fd.append("description", f.description || "");
      if (f.due) fd.append("due", f.due);
      if (f.grace) fd.append("grace", f.grace);
      f.rubric.filter((r) => r.criterion.trim()).forEach((r, i) => { fd.append(`rubric[${i}][criterion]`, r.criterion.trim()); fd.append(`rubric[${i}][marks]`, r.marks); });
      if (file) fd.append("file", file);
      await client.post(`/staff/classes/${cls.id}/assignment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Assignment saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "We couldn't save the assignment."));
    }
    setBusy(false);
  };
  const remove = async () => {
    if (!a) { setF(null); return; }
    try { await client.delete(`/staff/classes/${cls.id}/assignment`); toast.success("Assignment removed"); onSaved(); } catch (e) { toast.error(errMsg(e, "We couldn't remove it.")); }
  };
  const total = f.rubric.reduce((n, r) => n + (+r.marks || 0), 0);
  return (
    <div className="sp-assign">
      <p className="sp-lbl" style={{ margin: 0 }}>Assignment from this class{a?.submissions ? ` · ${a.submissions} submitted` : ""}</p>
      <div className="sp-form two">
        <label className="sp-field" htmlFor={`as-t-${cls.id}`}>Title<input id={`as-t-${cls.id}`} value={f.title} onChange={(e) => set("title", e.target.value)} /></label>
        <label className="sp-field" htmlFor={`as-f-${cls.id}`}>Assignment file (optional){a?.file_url && <a className="link-btn" href={a.file_url} target="_blank" rel="noopener noreferrer">Current file</a>}<input id={`as-f-${cls.id}`} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} /></label>
        <label className="sp-field" htmlFor={`as-d-${cls.id}`}>Due (WAT)<input id={`as-d-${cls.id}`} type="datetime-local" value={f.due} onChange={(e) => set("due", e.target.value)} /></label>
        <label className="sp-field" htmlFor={`as-g-${cls.id}`}>Late submissions until (WAT)<input id={`as-g-${cls.id}`} type="datetime-local" value={f.grace} onChange={(e) => set("grace", e.target.value)} /></label>
      </div>
      <label className="sp-field" htmlFor={`as-x-${cls.id}`}>Instructions<textarea id={`as-x-${cls.id}`} rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="What students should do and hand in" /></label>
      <p className="sp-lbl" style={{ margin: "6px 0 0" }}>Rubric · {total} marks</p>
      <div className="sp-rubric">
        {f.rubric.map((r, i) => (
          <div key={i}><input aria-label="Criterion" value={r.criterion} onChange={(e) => setCrit(i, "criterion", e.target.value)} /><input aria-label="Marks" type="number" min="1" max="100" value={r.marks} onChange={(e) => setCrit(i, "marks", e.target.value)} /><button type="button" className="link-btn danger" onClick={() => set("rubric", f.rubric.filter((_, j) => j !== i))}>Remove</button></div>
        ))}
      </div>
      <div className="sp-acts"><button type="button" className="link-btn" onClick={() => set("rubric", [...f.rubric, { criterion: "", marks: 5 }])}>+ Add criterion</button><span style={{ marginLeft: "auto" }} />
        <button type="button" className="link-btn danger" onClick={remove}>Remove assignment</button><button type="button" className="cbtn go" onClick={save} disabled={busy || !f.title.trim()}>{busy ? "Saving…" : "Save assignment"}</button></div>
    </div>
  );
}

/** The class recording, to watch here (no attendance tracking for staff). Other hosts open in a new tab. */
function StaffRecording({ c }) {
  const embed = vimeoEmbed(c.recording_link);
  const file = !embed && isVideoFile(c.recording_link);
  const frame = { position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, borderRadius: 12, background: "#000" };
  if (!embed && !file) return <a className="cbtn quiet" href={c.recording_link} target="_blank" rel="noopener noreferrer">Open the recording</a>;
  return (
    <div style={{ position: "relative", paddingTop: "56.25%", marginTop: 10 }}>
      {embed ? <iframe src={embed} title={`Recording: ${c.topic}`} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen style={frame} />
        : <video src={c.recording_link} controls style={frame} />}
    </div>
  );
}

function ClassCard({ c, onChanged, onMove, onDelete }) {
  const [watch, setWatch] = useState(false);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ topic: c.topic, minutes: c.minutes, link: c.link || "", description: c.description || "", recording_link: c.recording_link || "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ topic: c.topic, minutes: c.minutes, link: c.link || "", description: c.description || "", recording_link: c.recording_link || "" }); }, [c]);
  const save = async () => {
    setBusy(true);
    try { await client.patch(`/staff/classes/${c.id}`, { ...f, minutes: +f.minutes, recording_link: f.recording_link || null }); toast.success("Class saved"); onChanged(); }
    catch (e) { toast.error(errMsg(e, "We couldn't save the class.")); }
    setBusy(false);
  };
  const state = c.recording_link ? <Pill cls="ok">Recording posted</Pill> : c.past ? <Pill cls="watch">Recording to post</Pill> : <Pill cls="gray">Upcoming</Pill>;
  return (
    <article className="sp-cls-card">
      <div className="sp-cls-h">
        <span className="sp-fico big"><Icon name="video" /></span>
        <div><strong>{c.topic}</strong><small>{watWhen(c.start)} · {c.minutes / 60} h{c.session ? " · not counted for attendance" : ""}{c.makeup ? ` · ${c.makeup_of ? `for “${c.makeup_of.topic}” · ` : ""}${c.audience === "missed" ? "only students who missed it" : "everyone"} · attendance not tracked` : ""}</small></div>
        <span className="sp-acts">{c.makeup && <Pill cls="brand">Makeup class</Pill>}{c.session && <Pill cls="brand">Special session</Pill>}{state}</span>
        <span className="sp-acts">
          {c.recording_link && <button type="button" className="cbtn quiet" onClick={() => setWatch(!watch)}>{watch ? "Hide recording" : "Watch recording"}</button>}
          <button type="button" className="cbtn quiet" onClick={() => setOpen(!open)}>{open ? "Close" : "Edit"}</button>
          <button type="button" className="cbtn quiet" onClick={() => onMove(c)}>Change date</button>
        </span>
      </div>
      {watch && c.recording_link && <StaffRecording c={c} />}
      {!open ? (
        <div className="sp-cls-sum">
          {c.materials.length > 0 && <span><Icon name="files" size={14} /> {c.materials.length} material{c.materials.length > 1 ? "s" : ""}</span>}
          {c.assignment && <span><Icon name="task" size={14} /> {c.assignment.title}{c.assignment.rubric.length ? ` · ${c.assignment.rubric.reduce((n, r) => n + r.marks, 0)}-mark rubric` : ""}</span>}
          <span><Icon name="link" size={14} /> {c.link || "No class link yet"}</span>
        </div>
      ) : (
        <div className="sp-cls-form">
          <div className="sp-form two">
            <label className="sp-field" htmlFor={`c-t-${c.id}`}>Topic<input id={`c-t-${c.id}`} value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} /></label>
            <label className="sp-field" htmlFor={`c-l-${c.id}`}>Class link<input id={`c-l-${c.id}`} value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} placeholder="Zoom or Meet link" /></label>
            <label className="sp-field" htmlFor={`c-r-${c.id}`}>Recording link<input id={`c-r-${c.id}`} value={f.recording_link} onChange={(e) => setF({ ...f, recording_link: e.target.value })} placeholder="Vimeo link, added after class" /><small className="course-tag">Students who watch 60% get attendance.</small></label>
            <label className="sp-field" htmlFor={`c-m-${c.id}`}>Length<select id={`c-m-${c.id}`} value={f.minutes} onChange={(e) => setF({ ...f, minutes: e.target.value })}>{[60, 90, 120, 150, 180].map((m) => <option key={m} value={m}>{m / 60} h</option>)}</select></label>
          </div>
          <label className="sp-field" htmlFor={`c-d-${c.id}`}>What students should know (shown on the lesson)<textarea id={`c-d-${c.id}`} rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Prep, what to install, what we'll cover" /></label>
          <div className="sp-acts" style={{ justifyContent: "flex-end" }}><button type="button" className="link-btn danger" onClick={() => onDelete(c)}>Delete class</button><span style={{ marginLeft: "auto" }} /><button type="button" className="cbtn go" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save class"}</button></div>
          <div><p className="sp-lbl">Class materials</p><Materials items={c.materials} removeUrl={(id) => `/staff/class-materials/${id}`} onChange={onChanged} /><div style={{ marginTop: 8 }}><AddMaterial url={`/staff/classes/${c.id}/materials`} onAdded={onChanged} /></div></div>
          <Assignment cls={c} onSaved={onChanged} />
        </div>
      )}
    </article>
  );
}

/** The week editor: classes (incl. makeup), materials, assignments with rubrics, quizzes. */
export default function StaffWeekPage() {
  const { weekId } = useParams();
  const navigate = useNavigate();
  const [w, setW] = useState(null);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");

  const load = useCallback(() => {
    client.get(`/staff/weeks/${weekId}`).then((r) => { setW(r.data); setTitle(r.data.week.title || ""); setError(null); })
      .catch((e) => setError(e?.response?.data?.message || "We couldn't load this week."));
  }, [weekId]);
  useEffect(() => { setW(null); load(); }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(msg || r.data.message); setModal(null); load(); }
    catch (e) { toast.error(errMsg(e, "That didn't work. Please try again.")); }
    setBusy(false);
  };
  // Reload after saving: orientation and special weeks are named by their title, so the label changes too.
  const saveTitle = () => { if (w && title !== (w.week.title || "")) client.patch(`/staff/weeks/${w.week.id}`, { title: title || null, summary: w.week.summary }).then(() => { toast.success("Week title saved"); load(); }).catch(() => toast.error("We couldn't save the title.")); };
  const changeType = (type) => act(() => client.patch(`/staff/weeks/${w.week.id}`, { type, title: title || null, summary: w.week.summary }));
  const newQuiz = async () => {
    try { const r = await client.post(`/staff/weeks/${w.week.id}/quizzes`, { title: `${w.week.label} quiz` }); navigate(`/dashboard/staff/quizzes/${r.data.id}`); }
    catch (e) { toast.error(errMsg(e, "We couldn't create the quiz.")); }
  };

  if (error) return <div className="lms"><div className="main single"><div className="view"><section className="complete-box"><div><strong>{error}</strong><small>Go back to the course plan and try again.</small></div><Link className="cbtn quiet" to="/dashboard/staff/plans">Back to plans</Link></section></div></div></div>;
  if (!w) return <div className="lms"><div className="main single"><div className="view"><div className="skeleton" style={{ height: 100 }} /><div className="skeleton" style={{ height: 340 }} /></div></div></div>;

  const wk = w.week;
  const planUrl = `/dashboard/staff/plans/${w.plan.cohort_id}/${w.plan.course_id}`;
  const firstDay = wk.starts_on;
  const lastDay = (() => { const d = new Date(`${wk.starts_on}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + 6); return d.toISOString().slice(0, 10); })();

  return (
    <div className="lms"><div className="main single" style={{ maxWidth: 1240 }}><div className="view">
      <div className="pagebar"><nav className="trail" aria-label="You are here"><Link to="/dashboard/staff/plans">Course plans</Link><span className="sep">›</span><Link to={planUrl}>Plan</Link><span className="sep">›</span><span className="here">{wk.label}</span></nav></div>
      <div className="sp-top">
        <div><span className="sp-kicker">{wk.type === "week" ? "Teaching week" : wk.label}</span><h1>{wk.label}</h1>
          <input className="sp-wtitle" aria-label="Week title" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle}
            placeholder={{ week: "Add a week title (optional)", orientation: "Name this week, e.g. Kick off (optional)", event: "Name this week, e.g. Industry webinar week", break: "Name this break (optional)", capstone: "e.g. Capstone build (optional)" }[wk.type] || "Add a week title (optional)"} />
          <p>{watDay(`${wk.starts_on}T00:00`)} – {watDay(`${lastDay}T00:00`)} · times in WAT</p>
          {w.can?.change_type && (
            <label className="sp-field sp-wtype" htmlFor="wk-type">Type of week
              <select id="wk-type" value={wk.type} disabled={busy} onChange={(e) => changeType(e.target.value)}>
                {Object.entries(w.types || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
          )}</div>
        <div className="sp-filters">
          <button className="cbtn quiet" type="button" disabled={!w.prev} onClick={() => navigate(`/dashboard/staff/weeks/${w.prev.id}`)}>‹ {w.prev ? w.prev.label : "Previous"}</button>
          <button className="cbtn quiet" type="button" disabled={!w.next} onClick={() => navigate(`/dashboard/staff/weeks/${w.next.id}`)}>{w.next ? w.next.label : "Next"} ›</button>
        </div>
      </div>

      {wk.type === "break" && !w.classes.length ? (
        <section className="sp-note">Break week: no classes, and students see "Break" in their week list. To teach this week instead, change its type above; to pull later weeks back, remove it from the plan.</section>
      ) : null}

      <section className="sp-box">
        <div className="sp-box-h"><h2>Classes</h2><span className="sp-acts">
          <button className="cbtn quiet" type="button" onClick={() => setModal({ kind: "add", makeup: true, date: firstDay, time: "19:00", minutes: 120, topic: "", link: "", for: w.past_classes[0]?.id || "", audience: "missed" })}>+ Add makeup class</button>
          <button className="cbtn quiet" type="button" onClick={() => setModal({ kind: "add", makeup: false, session: true, date: firstDay, time: "19:00", minutes: 60, topic: "", link: "" })}>+ Add special session</button>
          <button className="cbtn quiet" type="button" onClick={() => setModal({ kind: "add", makeup: false, date: firstDay, time: "19:00", minutes: 120, topic: "", link: "" })}>+ Add class</button>
        </span></div>
        <div className="sp-box-b sp-stack">
          {w.classes.length ? w.classes.map((c) => <ClassCard key={c.id} c={c} onChanged={load} onMove={(x) => setModal({ kind: "move", c: x, start: x.start, due: true })} onDelete={(x) => setModal({ kind: "delete", c: x })} />)
            : <p className="course-tag" style={{ margin: 0 }}>No classes this week.</p>}
        </div>
      </section>

      <div className="sp-two">
        <section className="sp-box">
          <div className="sp-box-h"><h2>Quizzes</h2><button className="cbtn quiet" type="button" onClick={newQuiz}>+ New quiz</button></div>
          <div className="sp-rows">
            {w.quizzes.map((q) => (
              <button key={q.id} type="button" onClick={() => navigate(`/dashboard/staff/quizzes/${q.id}`)}>
                <span className="sp-fico big"><Icon name="assess" /></span>
                <div><strong>{q.title}</strong><small>{q.questions} questions · {q.minutes || "—"} min · {q.attempts || "—"} attempts · pass {q.pass}%{q.closes_at ? ` · closes ${watDay(q.closes_at)}` : ""}</small></div>
                {q.published ? <Pill cls="ok">Open to students</Pill> : <Pill cls="gray">Draft</Pill>}
              </button>
            ))}
            {!w.quizzes.length && <div><span /><small>No quiz this week.</small><span /></div>}
          </div>
        </section>
        <section className="sp-box">
          <div className="sp-box-h"><h2>Week materials</h2></div>
          <div className="sp-box-b sp-stack"><Materials items={w.materials} removeUrl={(id) => `/staff/week-materials/${id}`} onChange={load} /><AddMaterial url={`/staff/weeks/${wk.id}/materials`} onAdded={load} /></div>
        </section>
      </div>
      {w.projects.length > 0 && <section className="sp-note">This week has the capstone brief ({w.projects.map((p) => p.name).join(", ")}). Capstone &amp; teams gets its own page next.</section>}

      {modal?.kind === "add" && (
        <Modal title={modal.makeup ? "Add a makeup class" : modal.session ? "Add a special session" : "Add a class"} ok={modal.makeup ? "Add makeup class" : modal.session ? "Add session" : "Add class"} busy={busy} onCancel={() => setModal(null)}
          onSubmit={() => act(() => client.post(`/staff/weeks/${wk.id}/classes`, { topic: modal.topic || (modal.makeup ? `Makeup: ${w.past_classes.find((p) => p.id === +modal.for)?.topic || "class"}` : ""), start: `${modal.date}T${modal.time}`, minutes: +modal.minutes, link: modal.link || null, makeup: modal.makeup, session: !!modal.session, makes_up_for: modal.makeup && modal.for ? +modal.for : null, audience: modal.makeup && modal.for ? modal.audience : "all" }))}>
          {modal.session && <p className="course-tag" style={{ margin: 0 }}>A one-off session such as a webinar, guest talk or presentation. Students are notified; it doesn't count toward attendance.</p>}
          {modal.makeup && <p className="course-tag" style={{ margin: 0 }}>A makeup class repeats or replaces a class. Attendance isn't tracked; its recording is posted like any class. The students it's for get a notification.</p>}
          <div className="sp-form two">
            {modal.makeup && <label className="sp-field" htmlFor="ac-for">Makes up for<select id="ac-for" value={modal.for} onChange={(e) => setModal({ ...modal, for: e.target.value })}>{w.past_classes.map((p) => <option key={p.id} value={p.id}>{p.topic} · {watDay(p.start)}</option>)}{!w.past_classes.length && <option value="">No past classes yet</option>}</select></label>}
            {modal.makeup && modal.for && <label className="sp-field" htmlFor="ac-aud">Who it's for<select id="ac-aud" value={modal.audience} onChange={(e) => setModal({ ...modal, audience: e.target.value })}><option value="missed">Only students who missed that class</option><option value="all">Everyone in the course</option></select></label>}
            <label className="sp-field" htmlFor="ac-topic">Topic<input id="ac-topic" value={modal.topic} onChange={(e) => setModal({ ...modal, topic: e.target.value })} placeholder={modal.makeup ? "Leave blank to use \"Makeup: …\"" : "What this class covers"} autoFocus /></label>
            <label className="sp-field" htmlFor="ac-date">Date<input id="ac-date" type="date" min={wk.starts_on} max={lastDay} value={modal.date} onChange={(e) => setModal({ ...modal, date: e.target.value })} /></label>
            <label className="sp-field" htmlFor="ac-time">Start time (WAT)<input id="ac-time" type="time" value={modal.time} onChange={(e) => setModal({ ...modal, time: e.target.value })} /></label>
            <label className="sp-field" htmlFor="ac-len">Length<select id="ac-len" value={modal.minutes} onChange={(e) => setModal({ ...modal, minutes: e.target.value })}>{[60, 90, 120, 150, 180].map((m) => <option key={m} value={m}>{m / 60} h</option>)}</select></label>
            <label className="sp-field" htmlFor="ac-link">Class link<input id="ac-link" value={modal.link} onChange={(e) => setModal({ ...modal, link: e.target.value })} placeholder="Zoom or Meet link" /></label>
          </div>
        </Modal>
      )}
      {modal?.kind === "move" && (
        <Modal title={`Change the date of "${modal.c.topic}"`} ok="Change date" busy={busy} onCancel={() => setModal(null)}
          onSubmit={() => act(() => client.post(`/staff/classes/${modal.c.id}/move`, { start: modal.start, shift_due: modal.due }))}>
          <p className="course-tag" style={{ margin: 0 }}>Now: {watWhen(modal.c.start)}. Only this class moves; if the new date is in another week, it moves to that week.</p>
          <label className="sp-field" htmlFor="mc-s">New date and time (WAT)<input id="mc-s" type="datetime-local" value={modal.start} onChange={(e) => setModal({ ...modal, start: e.target.value })} autoFocus /></label>
          {modal.c.assignment && <label className="sp-chk" htmlFor="mc-due"><input id="mc-due" type="checkbox" checked={modal.due} onChange={(e) => setModal({ ...modal, due: e.target.checked })} /> Move its assignment's due date by the same amount</label>}
        </Modal>
      )}
      {modal?.kind === "delete" && (
        <Modal title={`Delete "${modal.c.topic}"?`} ok="Delete class" danger busy={busy} onCancel={() => setModal(null)}
          onSubmit={() => act(() => client.delete(`/staff/classes/${modal.c.id}`))}>
          <p>Its materials and assignment are deleted too. Classes students attended or submitted work for can't be deleted; change their date instead.</p>
        </Modal>
      )}
    </div></div></div>
  );
}
